'use client';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Guess, PuzzleResultsState } from '@wt/app/game/_components/types';
import { getGameScore } from '@wt/app/game/_utils/getGameScore';
import ImageModalToggle from '@wt/game/modals/ImageModal/toggle';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { PuzzleLocation } from '@wt/shared/components/maps/mapkit/types';
import { MapCoordinate } from '@wt/shared/components/maps/mapkit/types';
import MapElement from '@wt/shared/components/maps/mapkit/map';
import MapResults from '@wt/shared/components/maps/mapkit/map-results';
import { MapInteractionEvent } from '@wt/shared/libs/mapkit';
import { MapDistancePoints } from '@wt/shared/libs/mapkit/components/MapDistancePoints';
import { getSiteMode, hasHints } from '@wt/utilities/siteMode';
import { isNil } from 'lodash';
import { ImageProps } from 'next/image';
import Link from 'next/link';
import {
  memo,
  RefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import { createPortal } from 'react-dom';
import { CenterButton } from '../_components/CenterButton';
import { EDesktopGameView } from '../_components/roundGame/RoundGameDesktop';
import { ZoomInOutButtons } from '../_components/ZoomInOutButtons';
import { useHints } from '@wt/game/providers/hints/HintsProvider';
import { TeuteufTooltip } from '@wt/game/providers/tooltips';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import ModalLayout from '@wt/game/modals/layouts/ModalLayout';
import { Tooltip } from '@mui/material';
import { smallButtonClasses } from '@wt/shared/components/buttons/SmallButton';
import PlaceIconWithBorder from '@wt/shared/assets/PlaceIconWithBorder';

const HintWalletButtonWithTooltip = (props: {
  results: PuzzleResultsState;
  mode: 'results' | 'game';
}) => {
  const hints = useHints();
  const [open, setOpen] = useState(true);
  const [timePassed, setTimePassed] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTimePassed(true);
    }, 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const lastGuess =
    props.results.currentRound && props.results.currentRound >= 1
      ? props.results.guesses?.[props.results.currentRound - 1]
      : null;
  const usedProximityPic = !!lastGuess?.hintsUsed?.includes('proximitypic');

  const hasUsedHint = hints.getUsedHints().length > 0;

  return (
    <div
      onClick={() => {
        setOpen(false);
        hints.setHintsModalOpen(true);
      }}
      className={`mr-4 relative ${smallButtonClasses}`}
    >
      {props.mode == 'game' && (
        <TeuteufTooltip
          id="hint"
          onceOnly={true}
          active={props.results.round == 1 && !hasUsedHint && open}
          text="Try using the hints if you need a hand!"
        >
          <LightbulbOutlinedIcon htmlColor="black" />
        </TeuteufTooltip>
      )}
      {props.mode == 'results' && (
        <TeuteufTooltip
          id="hint-results-nomodal"
          maxWidth={420}
          onceOnly={false}
          okButtonText="Dismiss"
          additionalButtonText="Show Hint Wallet"
          additionalButtonOnClick={() => {
            hints.setHintsModalOpen(true);
          }}
          active={timePassed && usedProximityPic && open}
          text="Find data about your Hint photo in the Hint Wallet."
        >
          <LightbulbOutlinedIcon htmlColor="black" />
        </TeuteufTooltip>
      )}
    </div>
  );
};

const HintWalletButton = () => {
  const hints = useHints();
  return (
    <div
      onClick={() => {
        hints.setHintsModalOpen(true);
      }}
      className={`mr-4 relative ${smallButtonClasses}`}
    >
      <LightbulbOutlinedIcon htmlColor="black" />
    </div>
  );
};

export const ImageBlurLoaded = memo(function ImageBlurLoaded(
  props: ImageProps & {
    onClick?: () => void;
    panning: boolean;
    view?: EDesktopGameView;
    blur?: boolean;
  }
) {
  const imageRef = useRef<SVGImageElement | null>(null);

  const { resetTransform } = useControls();

  useEffect(() => {
    if (props.view !== undefined) {
      resetTransform(0);
    }
  }, [props.view]);

  useEffect(() => {
    if (imageRef.current && imageRef.current instanceof SVGImageElement) {
      imageRef.current.classList.remove('opacity-0');
    }
  }, [imageRef.current]);

  const cursorClass = props.panning ? 'cursor-grabbing' : '';

  return (
    <div
      className={`pointer-events-auto relative flex size-full items-center justify-center ${cursorClass}`}
      onDoubleClick={props.onClick}
    >
      <TransformComponent
        wrapperStyle={{
          width: '100%',
          height: '100%',
        }}
        contentStyle={{
          width: '100%',
          height: '100%',
        }}
      >
        <img
          src={props.src.toString()}
          alt=""
          className={`size-full bg-base-200 object-contain object-center transition-all duration-300 ease-linear ${
            props.blur ? 'blur-lg' : ''
          }`}
        />
      </TransformComponent>
    </div>
  );
});

export const GameImageElement = memo(function GameImageElement(props: {
  ref?: RefObject<HTMLImageElement>;
  puzzle: PuzzleLocation;
  view?: EDesktopGameView;
  onClickCenter?: () => void;
  mode: 'game' | 'results';
  results: PuzzleResultsState;
  blur?: boolean;
}) {
  const roundProgress = useRoundProgress();

  const isVideo = props.puzzle.url.indexOf('.mp4') !== -1;

  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const showHintButton = roundProgress.gameMode != 'community';

  const [panning, setPanning] = useState(false);

  let reportFormUrl = `https://docs.google.com/forms/d/e/1FAIpQLSdsHbUnmGS_XhzFAkvktBWasMqoHSBXxsl7zQ6NOb0N5MLrIg/viewform?usp=pp_url&entry.713331878=${roundProgress.puzzleId}&entry.864956852=${roundProgress.currentRound}`;
  if (getSiteMode() == 'wheretaken') {
    reportFormUrl = `https://docs.google.com/forms/d/e/1FAIpQLSfNtoQP_iLgjCS-cryRS-7rRU2rNW-UONpBC82hEwQTNtwmYg/viewform?usp=pp_url&entry.713331878=${roundProgress.puzzleId}&entry.864956852=${roundProgress.currentRound}`;
  } else if (getSiteMode() == 'whentaken-movies') {
    reportFormUrl = `https://docs.google.com/forms/d/e/1FAIpQLSePu-h8x5QFHqcEiAd7vLyamDkbUO8TlbY8y2HRrWr4BjXkxw/viewform?usp=pp_url&entry.713331878=${roundProgress.puzzleId}&entry.864956852=${roundProgress.currentRound}`;
  }

  return (
    <TransformWrapper
      disablePadding={true}
      centerOnInit={true}
      doubleClick={{ disabled: true }}
      onPanningStart={() => {
        setPanning(true);
      }}
      onPanningStop={() => {
        setPanning(false);
      }}
      wheel={{
        smoothStep: 0.01,
      }}
      pinch={{
        step: 0.01,
      }}
      maxScale={25}
    >
      <div className="relative size-full">
        {props.puzzle && (
          <div className="absolute inset-0 " ref={imageContainerRef}>
            <div
              className={
                isVideo
                  ? 'relative box-border size-full max-h-full max-w-full'
                  : props.puzzle.width > props.puzzle.height
                    ? 'relative box-border h-full md:w-auto'
                    : 'relative box-border size-full'
              }
            >
              {isVideo ? (
                <div className="relative inset-0 size-full max-h-full max-w-full overflow-hidden rounded-md bg-black  md:rounded-xl">
                  <video
                    ref={videoRef}
                    src={props.puzzle.url}
                    muted={true}
                    className="size-full bg-black"
                    loop={true}
                    autoPlay={true}
                    controls
                  ></video>
                </div>
              ) : (
                <div className="rounded-border-with-shadow relative inset-0 size-full max-h-full max-w-full overflow-hidden rounded-md md:rounded-xl">
                  <ImageBlurLoaded
                    src={props.puzzle.url}
                    alt={''}
                    onClick={() => {
                      const target =
                        document.getElementById('puzzle-image-modal');
                      if (target && target instanceof HTMLElement) {
                        target.click();
                      }
                    }}
                    height={props.puzzle.height}
                    width={props.puzzle.width}
                    panning={panning}
                    view={props.view}
                    blur={props.blur}
                  />
                </div>
              )}
              <div className="absolute right-4 top-4 z-10">
                {showHintButton && hasHints() && (
                  <>
                    <HintWalletButtonWithTooltip
                      mode={props.mode}
                      results={props.results}
                    />
                  </>
                )}
                <Link
                  href={reportFormUrl}
                  target="_blank"
                  className={`mr-4 ${smallButtonClasses}`}
                >
                  <WarningAmberRoundedIcon htmlColor="black" />
                </Link>
                {!props.blur && (
                  <ImageModalToggle
                    htmlFor={'puzzle-image-modal'}
                    className={`mr-4 ${smallButtonClasses}`}
                  >
                    <FullscreenRoundedIcon htmlColor="black" />
                  </ImageModalToggle>
                )}
                {!isNil(props.view) && !isNil(props.onClickCenter) && (
                  <CenterButton
                    isCentered={props.view === EDesktopGameView.image}
                    onClick={props.onClickCenter}
                    margin={'mr-4'}
                  />
                )}
                {!isVideo && <ZoomInOutButtons />}
              </div>
            </div>
          </div>
        )}
      </div>
    </TransformWrapper>
  );
});

export const GameMapElement = (props: {
  puzzle: PuzzleLocation;
  selectedLocation?: MapCoordinate;
  selectedLocationSecondary?: MapCoordinate;
  onLocationSelect?: (location: MapInteractionEvent) => void;
  onLocationSelectSecondary?: (location: MapInteractionEvent) => void;
  isCentered?: boolean;
  showHintButton?: boolean;
  onClickCenter?: () => void;
}) => {
  const hints = useHints();
  const [currentPin, setCurrentPin] = useState(0);
  const [prevHasUsedDualDrop, setPrevHasUsedDualDrop] = useState(false);

  const hasUsedDualDrop = hints.hasUsedHint('dualdrop');

  if (hasUsedDualDrop && !prevHasUsedDualDrop) {
    // The first time we activate dual drop, select the second pin
    setPrevHasUsedDualDrop(true);
    setCurrentPin(1);
  } else if (!hasUsedDualDrop && currentPin == 1) {
    setCurrentPin(0);
  }

  return (
    <>
      {createPortal(
        <ModalLayout htmlFor="map-modal">
          <GameMapElementInner
            inModal={true}
            currentPin={currentPin}
            setCurrentPin={setCurrentPin}
            puzzle={props.puzzle}
            selectedLocation={props.selectedLocation}
            selectedLocationSecondary={props.selectedLocationSecondary}
            onLocationSelect={props.onLocationSelect}
            onLocationSelectSecondary={props.onLocationSelectSecondary}
            isCentered={props.isCentered}
            showHintButton={props.showHintButton}
            onClickCenter={props.onClickCenter}
          />
        </ModalLayout>,
        document.body
      )}
      <GameMapElementInner
        inModal={false}
        currentPin={currentPin}
        setCurrentPin={setCurrentPin}
        puzzle={props.puzzle}
        selectedLocation={props.selectedLocation}
        selectedLocationSecondary={props.selectedLocationSecondary}
        onLocationSelect={props.onLocationSelect}
        onLocationSelectSecondary={props.onLocationSelectSecondary}
        isCentered={props.isCentered}
        showHintButton={props.showHintButton}
        onClickCenter={props.onClickCenter}
      />
    </>
  );
};

const GameMapElementInner = (props: {
  currentPin: number;
  setCurrentPin: (n: number) => void;
  inModal: boolean;
  puzzle: PuzzleLocation;
  selectedLocation?: MapCoordinate;
  selectedLocationSecondary?: MapCoordinate;
  onLocationSelect?: (location: MapInteractionEvent) => void;
  onLocationSelectSecondary?: (location: MapInteractionEvent) => void;
  isCentered?: boolean;
  showHintButton?: boolean;
  onClickCenter?: () => void;
}) => {
  const hints = useHints();
  const { currentPin, setCurrentPin } = props;

  const hasUsedDualDrop = hints.hasUsedHint('dualdrop');

  const onSelectLocation = useCallback(
    (e: MapInteractionEvent) => {
      if (currentPin == 1) {
        props.onLocationSelectSecondary && props.onLocationSelectSecondary(e);
      } else {
        props.onLocationSelect && props.onLocationSelect(e);
      }
    },
    [currentPin, props.onLocationSelect, props.onLocationSelectSecondary]
  );

  const MapMemo = useMemo(() => {
    return (
      <MapElement
        currentPin={currentPin}
        setCurrentPin={setCurrentPin}
        selectedLocation={props.selectedLocation}
        selectedLocationSecondary={props.selectedLocationSecondary}
        onLocationSelect={onSelectLocation}
        inModal={props.inModal}
        label={'Your Guess'}
      />
    );
  }, [
    onSelectLocation,
    props.selectedLocation,
    props.selectedLocationSecondary,
    props.onLocationSelect,
    props.onLocationSelectSecondary,
    props.inModal,
  ]);

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <div
          className={
            props.inModal
              ? 'absolute inset-0'
              : 'rounded-border-with-shadow absolute size-full grow bg-[#8CD4F5]'
          }
        >
          {MapMemo}
        </div>
      </Suspense>
      <div className="absolute right-4 top-4 z-10">
        {props.showHintButton && hasHints() && <HintWalletButton />}
        {!props.inModal && (
          <label htmlFor="map-modal" className={smallButtonClasses}>
            <FullscreenRoundedIcon htmlColor="black" />
          </label>
        )}
        {props.inModal && (
          <label htmlFor="map-modal" className={smallButtonClasses}>
            <FullscreenExitRoundedIcon htmlColor="black" />
          </label>
        )}
        {!props.inModal &&
          !isNil(props.isCentered) &&
          !isNil(props.onClickCenter) && (
            <CenterButton
              isCentered={props.isCentered}
              onClick={props.onClickCenter}
              margin={'ml-4'}
            />
          )}
        {hasUsedDualDrop && (
          <>
            <label
              className={
                `ml-4 ${smallButtonClasses} rounded-r-none ` +
                (currentPin == 0
                  ? '!border-[#322E38] !bg-[#F6F0FE] movies:!border-[#FFC800] movies:!bg-[#FEFBF0] movies:shadow-[0_0_4px_#FFC800] '
                  : '')
              }
              style={
                currentPin == 0 && getSiteMode() != 'whentaken-movies'
                  ? {
                      boxShadow: '0px 0px 4px rgba(106, 9, 246, 0.8)',
                    }
                  : {}
              }
              onClick={() => {
                setCurrentPin(0);
              }}
            >
              <PlaceIconWithBorder
                fontSize="medium"
                className="text-accent"
                stroke={
                  getSiteMode() == 'whentaken-movies' ? '#C1A001' : '#4C129F'
                }
              />
            </label>
            <label
              className={
                `${smallButtonClasses} rounded-l-none ` +
                (currentPin == 1
                  ? '!border-[#322E38] !bg-[#F6F0FE] movies:!border-[#FFC800] movies:!bg-[#FEFBF0] movies:shadow-[0_0_4px_#FFC800] '
                  : '')
              }
              style={
                currentPin == 1 && getSiteMode() != 'whentaken-movies'
                  ? {
                      boxShadow: '0px 0px 4px rgba(106, 9, 246, 0.8)',
                    }
                  : {}
              }
              onClick={() => {
                setCurrentPin(1);
              }}
            >
              <PlaceIconWithBorder
                fontSize="medium"
                className="text-error"
                stroke="#C63D40"
              />
            </label>
          </>
        )}
      </div>
    </>
  );
};

interface MapDistanceElementProps {
  id: string;
  guess: Guess;
  to: {
    longitude: number;
    latitude: number;
  };
  showHintButton?: boolean;
}

export function GameMapDistanceElement(props: MapDistanceElementProps) {
  const froms = props.guess.locationSecondary
    ? [
        [props.guess.location.latitude, props.guess.location.longitude],
        [
          props.guess.locationSecondary.latitude,
          props.guess.locationSecondary.longitude,
        ],
      ]
    : [[props.guess.location.latitude, props.guess.location.longitude]];
  return (
    <div className="rounded-border-with-shadow absolute size-full grow">
      <MapResults>
        <MapDistancePoints
          froms={froms}
          to={[props.to.latitude, props.to.longitude]}
        />
      </MapResults>
      <div className="absolute top-4 right-4 z-[98]">
        {props.showHintButton && <HintWalletButton />}
        <label htmlFor={props.id} className={smallButtonClasses}>
          <FullscreenRoundedIcon htmlColor="black" />
        </label>
      </div>
    </div>
  );
}

export const RoundResultsSummary = memo(
  (props: { lastGuess: Guess; unit: string }) => {
    const puzzle = props.lastGuess.puzzle;

    const locationParts = [];
    if (puzzle.locationName) locationParts.push(puzzle.locationName.trim());
    if (puzzle.city) locationParts.push(puzzle.city.trim());
    if (puzzle.country) locationParts.push(puzzle.country.trim());
    const locationDescription = locationParts.join(', ');

    const roundScores = getGameScore(props.lastGuess, props.unit);

    return (
      <div className="flex flex-col">
        {getSiteMode() != 'wheretaken' && (
          <>
            <div className="flex gap-3">
              <div className="text-3.5xl" data-animate-element="year-icon">
                🗓️
              </div>
              <div>
                <div
                  className="text-3.5xl font-bold flex items-center gap-1"
                  data-animate-element="year-text"
                >
                  {puzzle.yearBuffer ? (
                    <Tooltip
                      title={`Our researchers couldn’t ascertain an exact year for this scene, so we accepted anything in this range.`}
                      arrow
                    >
                      <span>
                        {puzzle.year - puzzle.yearBuffer} &ndash;{' '}
                        {puzzle.year + puzzle.yearBuffer}
                      </span>
                    </Tooltip>
                  ) : (
                    <span>{puzzle.year}</span>
                  )}
                </div>
                <div
                  className="font-bold text-highlight"
                  data-animate-element="year-guess"
                >
                  {roundScores.yearsOff == 1 && <>1 year off</>}
                  {roundScores.yearsOff != 1 && (
                    <>{roundScores.yearsOff} years off</>
                  )}
                  <span className="ml-3 bg-base-200 font-normal text-sm py-1 px-2 rounded-lg text-primary-color movies:dark:text-white">
                    your guess: {props.lastGuess.year}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4"></div>
          </>
        )}
        <div className="flex gap-3">
          <div className="text-3.5xl" data-animate-element="location-icon">
            📍
          </div>
          <div>
            <div
              className="text-xl font-semibold uppercase"
              data-animate-element="location-text"
            >
              {locationDescription}
            </div>
            <div
              className="font-bold text-highlight"
              data-animate-element="location-guess"
            >
              {roundScores.distanceText} away
            </div>
          </div>
        </div>
        <div className="mt-6"></div>
        <div data-animate-element="description">{puzzle.description}</div>
        {getSiteMode() == 'whentaken-movies' && (
          <>
            <div className="mt-2"></div>
            <div
              className="text-lg font-semibold"
              data-animate-element="description"
            >
              🎬 {puzzle.filmName}, {puzzle.filmReleaseYear}
              {puzzle.info_url && (
                <>
                  {' '}
                  &ndash;{' '}
                  <a
                    href={puzzle.info_url}
                    target="_blank"
                    className="underline font-normal cursor-pointer"
                  >
                    Look it up on IMDb
                  </a>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
);
